import type { KrokResponse } from "./types"
import { AuthManager } from "../auth/AuthManager"
import type { Sticker } from "../state/AppState"

export class HttpError extends Error {
  status: number
  code?: string

  constructor(status: number, code?: string) {
    super(code ?? `HTTP ${status}`)
    this.status = status
    this.code = code
  }
}

export interface CompleteResponse {
  api_version: string
  status: "recorded"
  next_state: "available" | "locked" | "finished"
}

export interface RepeatItem {
  krok_id: string
  krok_title: string
  lesson_id: string
  lesson_title: string
  lesson_order: number
  krok_order: number
}

export interface RepeatResponse {
  api_version: string
  items: RepeatItem[]
}

export interface SubscribeResponse {
  redirect_url: string
  order_id: string
}

// ===============================
// SCREEN CONTRACT (V6 FINAL)
// ===============================

export type Screen =
  | {
      type: "krok"
      id: string
      title: string
      tasks: any[]
      next_krok_id: string | null
      prev_krok_id: string | null
    }
  | {
      type: "notice"
      noticeType: "next" | "locked" | "finished"
      nextKrokId?: string | null
    }
  | {
      type: "account"
      reason: "expired"
    }
  | {
    type: "subscriptions"
    available_stickers: Sticker[]
  }

  | {
    type: "confirmChange"
    remaining_days: number
    current_sticker: any
    new_sticker: any
  }

export type EntryResponse = {
  screen: Screen
}

export class ApiClient {
  private readonly baseUrl: string
  private readonly apiPrefix = "/wp-json/edu/v6"
  private readonly auth: AuthManager

  constructor(baseUrl: string, auth: AuthManager) {
    this.baseUrl = baseUrl
    this.auth = auth
  }

  private async request<T>(
    path: string,
    options: RequestInit,
    retry = true,
    requireAuth = true
  ): Promise<T> {

    // 🔥 КЛЮЧЕВОЕ ИСПРАВЛЕНИЕ
    if (requireAuth) {
      await this.auth.waitUntilAuthenticated()
    }

    const headers: Record<string, string> = {
      "Accept": "application/json",
      ...(options.headers as Record<string, string> ?? {})
    }

    const token = this.auth.getAccessToken()

    if (token && requireAuth) {
      headers["Authorization"] = `Bearer ${token}`
    }

    const response = await fetch(
      `${this.baseUrl}${this.apiPrefix}${path}`,
      {
        ...options,
        headers
      }
    )

    if (response.status === 401 && retry && requireAuth) {
      await this.auth.handle401()
      return this.request<T>(path, options, false, requireAuth)
    }

    if (!response.ok) {
      throw new HttpError(response.status)
    }

    return response.json()
  }

  private async postPublic<T>(path: string, body: unknown): Promise<T> {
  return this.request<T>(
    path,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    },
    true,
    false // 🔥 БЕЗ AUTH
  )
}

  private async get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: "GET" })
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    })
  }

  // ===============================
  // 🔥 SINGLE ENTRY POINT (BOOT)
  // ===============================

  async getEntry(): Promise<EntryResponse> {
    return this.get("/entry")
  }

  // ===============================
  // OTHER ENDPOINTS
  // ===============================

  async getRepeat(): Promise<RepeatResponse> {
    return this.get("/repeat")
  }

  async subscribe(
    stickerId: string,
    currency: "EUR" | "UAH",
    force: boolean = false
  ): Promise<any> {

    return this.post("/subscribe", {
      sticker_id: stickerId,
      currency,
      ...(force ? { force: true } : {})
    })

  }

  async confirmChange(
      stickerId: string
    ): Promise<any> {

      return this.post(
        "/confirm-change",
        {
          sticker_id: stickerId
        }
      )
    }

  // ===============================
  // NEW V6 ENDPOINTS
  // ===============================

  async getCompletedList(): Promise<{
        screen: {
          type: "list",
          products: any[]
        }
      }>
 {
    return this.get("/completed-list")
  }

  async complete(krokId: string): Promise<{ screen: Screen }> {
    return this.post("/complete", {
      krok_id: krokId
    })
  }

  async navigate(krokId: string): Promise<{ screen: Screen }> {
    return this.post("/navigate", {
      krok_id: krokId
    })
  }

  async entryKrok(): Promise<{ screen: Screen }> {
    return this.get("/entry")
  }

  // ===============================
  // SUBSCRIPTION REDIRECT URL
  // ===============================

  async getSubscription(
    productId: string
  ): Promise<{
    screen: {
      type: "subscriptions"
      available_stickers: Sticker[]
    }
  }> {

    return this.get(
      `/subscription?product_id=${encodeURIComponent(productId)}`
    )
  }
  // ===============================
  // ACCOUNT
  // ===============================

  async getAccount(): Promise<{
    type: "account"

    email: string

    products: {
      product_id: string
      title: string
      landing_url: string

      state:
        | "active"
        | "recoverable"
        | "never_subscribed"

      remaining_new_kroks: number

    subscriptions: {
      subscription_id: string | null

      renewal_active: boolean

      daily_krok_limit: number

      remaining_days: number

      next_payment_date: string
    }[]
    }[]
  }>
  {
    return this.get("/account")
  }

  async cancelSubscription(
      subscriptionId: string
    ): Promise<any> {

      return this.post(
        "/cancel-subscription",
        {
          subscription_id: subscriptionId
        }
      )
    }
  
  
}